import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Trash2, MapPin, Clock, FileText, ArrowRight, Plus } from 'lucide-react';
import { API, authFetch } from '../utils/api';

const localizer = momentLocalizer(moment);

const LOCATIONS = [
  { value: 'osf', label: 'OSF' },
  { value: 'civic_center', label: 'Civic Center' },
  { value: 'civic_center_west', label: 'Civic Center West' },
  { value: 'great_park', label: 'Great Park' }
];

const HOURS = ['12', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11'];
const MINUTES = ['00', '15', '30', '45'];

function getLocalDate() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const defaultForm = {
  start_date: getLocalDate(),
  start_hour: '09',
  start_minute: '00',
  start_ampm: 'AM',
  end_date: getLocalDate(),
  end_hour: '05',
  end_minute: '00',
  end_ampm: 'PM',
  location: '',
  notes: ''
};

function from24(date) {
  const d = new Date(date);
  let h = d.getHours();
  const ampm = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return {
    hour: String(h).padStart(2, '0'),
    minute: String(d.getMinutes()).padStart(2, '0'),
    ampm
  };
}

function toISOLocal(dateStr, hour, minute, ampm) {
  let h = parseInt(hour);
  if (ampm === 'PM' && h !== 12) h += 12;
  else if (ampm === 'AM' && h === 12) h = 0;
  return `${dateStr}T${String(h).padStart(2, '0')}:${minute}:00`;
}

function compareTime(d1, d2) {
  return d1 < d2 ? -1 : d1 > d2 ? 1 : 0;
}

export default function ScheduleManagement({ user }) {
  const [schedules, setSchedules] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [formData, setFormData] = useState({ ...defaultForm });

  const location = useLocation();

  useEffect(() => {
    fetchSchedules();
    const interval = setInterval(fetchSchedules, 30000);
    const onFocus = () => fetchSchedules();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(interval); window.removeEventListener('focus', onFocus); };
  }, [location.key]);

  const fetchSchedules = async () => {
    try {
      const response = await authFetch(`${API}/schedules`);
      if (!response.ok) throw new Error('Failed to fetch schedules');
      const data = await response.json();
      setSchedules(data);
    } catch (error) {
      toast.error('Failed to load schedules');
    }
  };

  const handleSelectSlot = (slotInfo) => {
    setSelectedSlot(slotInfo);
    setEditingSchedule(null);
    const startDate = moment(slotInfo.start).format('YYYY-MM-DD');

    setFormData({
      ...defaultForm,
      start_date: startDate,
      end_date: startDate,
    });
    setDialogOpen(true);
  };

  const handleSelectEvent = (event) => {
    const schedule = event.resource;
    setEditingSchedule(schedule);
    setSelectedSlot(null);

    const parseStoredDate = (isoStr) => {
      const clean = String(isoStr).replace('Z', '');
      const [datePart, timePart] = clean.split('T');
      if (!timePart) return { date: datePart, hour: '09', minute: '00', ampm: 'AM' };
      const [hh, mm] = timePart.split(':');
      let h = parseInt(hh);
      const ampm = h >= 12 ? 'PM' : 'AM';
      if (h === 0) h = 12;
      else if (h > 12) h -= 12;
      return { date: datePart, hour: String(h).padStart(2, '0'), minute: (mm || '00').slice(0, 2), ampm };
    };

    const start = parseStoredDate(schedule.scheduled_date);
    const endRaw = schedule.end_date || schedule.scheduled_date;
    const end = parseStoredDate(endRaw);

    setFormData({
      start_date: start.date,
      start_hour: start.hour,
      start_minute: start.minute,
      start_ampm: start.ampm,
      end_date: end.date,
      end_hour: end.hour,
      end_minute: end.minute,
      end_ampm: end.ampm,
      location: schedule.location || '',
      notes: schedule.notes || ''
    });
    setDialogOpen(true);
  };

  const getLocationLabel = (value) => {
    const loc = LOCATIONS.find(l => l.value === value);
    return loc ? loc.label : value || '';
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    const startISO = toISOLocal(formData.start_date, formData.start_hour, formData.start_minute, formData.start_ampm);
    const endISO = toISOLocal(formData.end_date, formData.end_hour, formData.end_minute, formData.end_ampm);

    if (compareTime(endISO, startISO) <= 0) {
      toast.error('End date/time must be after start date/time');
      return;
    }

    try {
      const url = editingSchedule
        ? `${API}/schedules/${editingSchedule.schedule_id}`
        : `${API}/schedules`;
      const method = editingSchedule ? 'PUT' : 'POST';

      const response = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scheduled_date: startISO,
          end_date: endISO,
          location: formData.location,
          notes: formData.notes,
          vehicle_id: 'schedule_only'
        })
      });

      if (!response.ok) throw new Error(editingSchedule ? 'Failed to update schedule' : 'Failed to create schedule');

      toast.success(editingSchedule ? 'Schedule updated successfully' : 'Wash scheduled successfully');
      setDialogOpen(false);
      setEditingSchedule(null);
      setFormData({ ...defaultForm });
      fetchSchedules();
    } catch (error) {
      toast.error(editingSchedule ? 'Failed to update schedule' : 'Failed to schedule wash');
    }
  };

  const handleDelete = async () => {
    if (!editingSchedule) return;
    if (!window.confirm('Are you sure you want to delete this schedule?')) return;

    try {
      const response = await authFetch(`${API}/schedules/${editingSchedule.schedule_id}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete schedule');

      toast.success('Schedule deleted');
      setDialogOpen(false);
      setEditingSchedule(null);
      setFormData({ ...defaultForm });
      fetchSchedules();
    } catch (error) {
      toast.error('Failed to delete schedule');
    }
  };

  const parseDate = (isoStr) => {
    const clean = String(isoStr).replace('Z', '');
    return new Date(clean);
  };

  const events = schedules.map(schedule => {
    const start = parseDate(schedule.scheduled_date);
    const end = schedule.end_date ? parseDate(schedule.end_date) : new Date(start.getTime() + 3600000);
    return {
      id: schedule.schedule_id,
      title: getLocationLabel(schedule.location) || 'Scheduled Wash',
      start,
      end,
      resource: schedule
    };
  });

  const eventStyleGetter = () => ({
    style: {
      backgroundColor: '#22c55e',
      borderRadius: '4px',
      opacity: 1,
      color: 'white',
      border: '0',
      display: 'block',
      fontWeight: '600',
      fontSize: '12px',
      padding: '2px 6px'
    }
  });

  const dayPropGetter = (date) => {
    const hasEvent = events.some(event => event.start.toDateString() === date.toDateString() || event.end.toDateString() === date.toDateString());
    if (hasEvent) {
      return { style: { backgroundColor: '#dcfce7', border: '2px solid #22c55e' } };
    }
    return {};
  };

  const TimeSelect = ({ prefix, data, onChange }) => (
    <div className="flex gap-2">
      <Select value={data[`${prefix}_hour`]} onValueChange={(v) => onChange({ ...data, [`${prefix}_hour`]: v })}>
        <SelectTrigger data-testid={`schedule-${prefix}-hour`} className="bg-slate-50 border-slate-200 w-20"><SelectValue placeholder="Hr" /></SelectTrigger>
        <SelectContent>{HOURS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
      </Select>
      <span className="flex items-center text-xl font-bold">:</span>
      <Select value={data[`${prefix}_minute`]} onValueChange={(v) => onChange({ ...data, [`${prefix}_minute`]: v })}>
        <SelectTrigger data-testid={`schedule-${prefix}-minute`} className="bg-slate-50 border-slate-200 w-20"><SelectValue placeholder="Min" /></SelectTrigger>
        <SelectContent>{MINUTES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={data[`${prefix}_ampm`]} onValueChange={(v) => onChange({ ...data, [`${prefix}_ampm`]: v })}>
        <SelectTrigger data-testid={`schedule-${prefix}-ampm`} className="bg-slate-50 border-slate-200 w-20"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="AM">AM</SelectItem>
          <SelectItem value="PM">PM</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );

  const openNewSchedule = () => {
    setEditingSchedule(null);
    setSelectedSlot(null);
    setFormData({
      ...defaultForm,
      start_date: getLocalDate(),
      end_date: getLocalDate(),
    });
    setDialogOpen(true);
  };

  return (
    <Layout user={user}>
      <div className="p-4 lg:p-12 space-y-6 lg:space-y-8 bg-slate-200 min-h-screen">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl lg:text-5xl font-heading font-bold tracking-tight" data-testid="schedule-title">Schedule Wash</h1>
          <Button onClick={openNewSchedule} data-testid="add-schedule-button">
            <Plus className="h-4 w-4 mr-2" />
            NEW SCHEDULE
          </Button>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-2 lg:p-6" style={{ height: 'calc(100vh - 200px)', minHeight: 400 }} data-testid="calendar-container">
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectEvent}
            selectable
            style={{ height: '100%' }}
            eventPropGetter={eventStyleGetter}
            dayPropGetter={dayPropGetter}
          />
        </div>

        {/* Upcoming Schedule List */}
        {schedules.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-md p-4 lg:p-6" data-testid="schedule-list">
            <h2 className="text-lg font-heading font-bold tracking-tight uppercase mb-4">Upcoming Schedules</h2>
            <div className="divide-y divide-slate-100">
              {[...schedules]
                .sort((a, b) => new Date(String(a.scheduled_date).replace('Z','')) - new Date(String(b.scheduled_date).replace('Z','')))
                .map((schedule) => {
                  const start = parseDate(schedule.scheduled_date);
                  const end = schedule.end_date ? parseDate(schedule.end_date) : null;
                  const startStr = start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
                  const startTime = start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                  const endStr = end ? end.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : null;
                  const endTime = end ? end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : null;
                  const sameDay = end && start.toDateString() === end.toDateString();

                  return (
                    <div
                      key={schedule.schedule_id}
                      className="py-3 cursor-pointer hover:bg-slate-50 rounded px-2 -mx-2 transition-colors"
                      onClick={() => handleSelectEvent({ resource: schedule })}
                      data-testid={`schedule-item-${schedule.schedule_id}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Clock className="h-4 w-4 text-slate-400 shrink-0" />
                          <span className="text-sm font-semibold text-slate-800">{startStr}</span>
                          <span className="text-sm text-slate-500">{startTime}</span>
                          {end && (
                            <>
                              <ArrowRight className="h-3 w-3 text-slate-400 shrink-0" />
                              {!sameDay && <span className="text-sm font-semibold text-slate-800">{endStr}</span>}
                              <span className="text-sm text-slate-500">{endTime}</span>
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-green-500 shrink-0" />
                          <span className="text-sm font-medium text-slate-700">{getLocationLabel(schedule.location)}</span>
                        </div>
                      </div>
                      {schedule.notes && (
                        <div className="flex items-center gap-2 text-slate-500 mt-1 ml-6">
                          <FileText className="h-3.5 w-3.5 shrink-0" />
                          <span className="text-sm truncate">{schedule.notes}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setEditingSchedule(null);
            setFormData({ ...defaultForm });
          }
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl uppercase">
                {editingSchedule ? 'Edit Schedule' : 'Schedule Wash'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* START */}
              <div className="space-y-2">
                <Label className="uppercase text-xs font-bold tracking-wide">Start Date</Label>
                <Input
                  data-testid="schedule-start-date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => {
                    const newStart = e.target.value;
                    const update = { ...formData, start_date: newStart };
                    if (newStart > formData.end_date) update.end_date = newStart;
                    setFormData(update);
                  }}
                  required
                  className="bg-slate-50 border-slate-200"
                />
              </div>
              <div className="space-y-2">
                <Label className="uppercase text-xs font-bold tracking-wide">Start Time</Label>
                <TimeSelect prefix="start" data={formData} onChange={setFormData} />
              </div>

              {/* END */}
              <div className="space-y-2">
                <Label className="uppercase text-xs font-bold tracking-wide">End Date</Label>
                <Input
                  data-testid="schedule-end-date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  min={formData.start_date}
                  required
                  className="bg-slate-50 border-slate-200"
                />
              </div>
              <div className="space-y-2">
                <Label className="uppercase text-xs font-bold tracking-wide">End Time</Label>
                <TimeSelect prefix="end" data={formData} onChange={setFormData} />
              </div>

              <div className="space-y-2">
                <Label className="uppercase text-xs font-bold tracking-wide">Location</Label>
                <Select value={formData.location} onValueChange={(value) => setFormData({ ...formData, location: value })} required>
                  <SelectTrigger data-testid="schedule-location-select" className="bg-slate-50 border-slate-200">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCATIONS.map((loc) => (
                      <SelectItem key={loc.value} value={loc.value}>{loc.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="uppercase text-xs font-bold tracking-wide">Notes</Label>
                <Textarea
                  data-testid="schedule-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Add any notes..."
                  className="bg-slate-50 border-slate-200"
                  rows={3}
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1 uppercase font-bold" data-testid="schedule-submit-button">
                  {editingSchedule ? 'Update Schedule' : 'Schedule Wash'}
                </Button>
                {editingSchedule && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleDelete}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="schedule-delete-button"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
